import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import {
  CATEGORY_ID_KEY,
  CATEGORY_KEY,
  PRODUCT_CATEGORY_KEY,
  PRODUCT_HISTORY_ID_KEY,
  PRODUCT_ID_KEY,
  PRODUCT_KEY,
  PRODUCT_STOCK_KEY,
  PRODUCT_V3_KEY,
  PRODUCT_V3_DETAIL_KEY,
  STOCKS_V3_KEY,
  STOCK_HISTORY_KEY,
  STOCK_HISTORY_V3_KEY,
  STOCK_HISTORY_STATISTIC_V3_KEY,
  INVENTORY_SETTINGS_KEY,
  STOCK_ID_KEY,
  STOCK_KEY,
  STOCK_STATISTIC_KEY,
  UNIT_KEY,
  WAREHOUSE_KEY,
} from 'common/constants/queryKeys'
import { TTL_5_MIN } from 'common/constants/staleTime'

import {
  getCategory,
  getCategoryById,
  getHistoryProductById,
  getHistoryStock,
  getProductById,
  getProductCategory,
  getProducts,
  getProductsStock,
  getProductsV3,
  getProductV3ById,
  getStock,
  getStockById,
  getStockStatistic,
  getStocksV3,
  getStockHistoryV3,
  getStockHistoryStatisticV3,
  getInventorySettings,
  getUnit,
  getWarehouse,
} from '.'
import { PagesQuery, ProductQuery, ProductV3Query, StockQuery, StockStatisticQuery, StockV3Query, StockHistoryV3Query } from './type'

export const ROWS_PER_PAGE = 20
export const INITIAL_PAGE_SIZE = 20
export const PAGE_STEP = 20

export const useCategory = (params: PagesQuery) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params.pageSize ?? 10,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...CATEGORY_KEY, cleanParams(params)],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getCategory(cleanParams(pageParam))

      return {
        categories: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
  })
}

export const useWarehouse = () => {
  const initParams: PagesQuery = {
    pageSize: INITIAL_PAGE_SIZE,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...WAREHOUSE_KEY],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getWarehouse(cleanParams(pageParam))
      return {
        warehouses: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
    keepPreviousData: true,
  })
}

export const useStock = (params: StockQuery, options?: { enabled?: boolean }) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params.pageSize ?? 10,
    page: 1,
  }
  return useInfiniteQuery({
    queryKey: [...STOCK_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getStock(cleanParams(pageParam))

      return {
        stocks: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
    enabled: options?.enabled ?? true,
    keepPreviousData: true,
  })
}

export const useStockById = (stockId?: string) => {
  return useQuery({
    queryKey: [...STOCK_ID_KEY, stockId],
    queryFn: async () => {
      const { data } = await getStockById(stockId!)
      return data.data
    },
    enabled: !!stockId,
    staleTime: TTL_5_MIN,
  })
}

export const useStockHistory = (params: StockQuery, options?: { enabled?: boolean }) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params.pageSize ?? 10,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...STOCK_HISTORY_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getHistoryStock(cleanParams(pageParam))
      return {
        stockHistorys: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
    enabled: options?.enabled ?? true,
    keepPreviousData: true,
  })
}

export const useUnits = () => {
  return useQuery({
    queryKey: UNIT_KEY,
    queryFn: async () => {
      const { data } = await getUnit()
      return data.data
    },
    staleTime: TTL_5_MIN,
  })
}

export const useProductCategory = () => {
  return useQuery({
    queryKey: PRODUCT_CATEGORY_KEY,
    queryFn: async () => {
      const { data } = await getProductCategory()
      return data.data
    },
    staleTime: TTL_5_MIN,
  })
}

export const useProduct = (params: ProductQuery) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params.pageSize ?? 10,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...PRODUCT_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getProducts(cleanParams(pageParam))

      return {
        products: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
  })
}

export const useProductStock = (params: ProductQuery) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params.pageSize ?? 10,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...PRODUCT_STOCK_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getProductsStock(cleanParams(pageParam))

      return {
        productStocks: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
    keepPreviousData: true,
  })
}

const cleanParams = <T extends Record<string, any>>(p: T) =>
  Object.fromEntries(
    Object.entries(p).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  ) as T

export const useInventoryCategoryById = (categoryId?: string) => {
  return useQuery({
    queryKey: [...CATEGORY_ID_KEY, categoryId],
    queryFn: async () => {
      const { data } = await getCategoryById(categoryId!)
      return data.data
    },
    enabled: !!categoryId,
    staleTime: TTL_5_MIN,
  })
}

export const useHistoryProductById = (productId?: string, params?: PagesQuery) => {
  const initParams: PagesQuery = {
    ...params,
    pageSize: params?.pageSize ?? 10,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...PRODUCT_HISTORY_ID_KEY, productId, params],
    queryFn: async ({ pageParam = initParams }) => {
      const safeParams = cleanParams(pageParam || {})
      const { data } = await getHistoryProductById(productId!, safeParams)

      return {
        productHistory: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          ...initParams,
          page: lastPage.nextPage,
        }
      }
      return undefined
    },
    enabled: !!productId,
    keepPreviousData: true,
  })
}

export const useProductById = (productId?: string) => {
  return useQuery({
    queryKey: [...PRODUCT_ID_KEY, productId],
    queryFn: async () => {
      const { data } = await getProductById(productId!)
      return data.data
    },
    enabled: !!productId,
    staleTime: TTL_5_MIN,
  })
}

export const useProductsV3 = (params: ProductV3Query) => {
  const initParams: ProductV3Query = {
    ...params,
    productStockStatus: 'ACTIVE',
    pageSize: params.pageSize ?? 20,
    page: 1,
  }

  return useInfiniteQuery({
    queryKey: [...PRODUCT_V3_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getProductsV3(cleanParams(pageParam))
      return {
        products: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return { ...initParams, page: lastPage.nextPage }
      }
      return undefined
    },
    keepPreviousData: true,
  })
}

export const useStockStatistic = (params?: StockStatisticQuery) => {
  return useQuery({
    queryKey: [...STOCK_STATISTIC_KEY, params],
    queryFn: async () => {
      const { data } = await getStockStatistic(params)
      return data.data
    },
    staleTime: TTL_5_MIN,
  })
}

export const useProductV3ById = (productId?: string) =>
  useQuery({
    queryKey: [...PRODUCT_V3_DETAIL_KEY, productId],
    queryFn: async () => {
      const { data } = await getProductV3ById(productId!)
      return data.data
    },
    enabled: !!productId,
    staleTime: TTL_5_MIN,
  })

export const useStocksV3 = (params: StockV3Query) => {
  const initParams = { ...params, pageSize: params.pageSize ?? 20, page: 1 }
  return useInfiniteQuery({
    queryKey: [...STOCKS_V3_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getStocksV3(cleanParams(pageParam))
      return { stocks: data.data, nextPage: data.nextPage }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage ? { ...initParams, page: lastPage.nextPage } : undefined,
    keepPreviousData: true,
  })
}

export const useStockHistoryV3 = (params: StockHistoryV3Query, options?: { enabled?: boolean }) => {
  const initParams = { ...params, pageSize: params.pageSize ?? INITIAL_PAGE_SIZE, page: 1 }
  return useInfiniteQuery({
    queryKey: [...STOCK_HISTORY_V3_KEY, params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getStockHistoryV3(cleanParams(pageParam))
      return { items: data.data, nextPage: data.nextPage }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage ? { ...initParams, page: lastPage.nextPage } : undefined,
    enabled: options?.enabled ?? true,
    keepPreviousData: true,
  })
}

export const useStockHistoryStatisticV3 = (params: Omit<StockHistoryV3Query, 'transactionType' | 'pageSize' | 'page'>) => {
  return useQuery({
    queryKey: [...STOCK_HISTORY_STATISTIC_V3_KEY, params],
    queryFn: async () => {
      const { data } = await getStockHistoryStatisticV3(cleanParams(params))
      return data.data
    },
    keepPreviousData: true,
  })
}

export const useInventorySettings = () =>
  useQuery({
    queryKey: INVENTORY_SETTINGS_KEY,
    queryFn: async () => {
      const { data } = await getInventorySettings()
      return data.data
    },
    staleTime: TTL_5_MIN,
  })
